import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, Clock, CheckCircle, Users, Download } from 'lucide-react';

const COLORS = ['#2E7D32', '#7B1FA2', '#1565C0', '#F9A825', '#00897B', '#D32F2F'];

export default function Reports() {
  const [metrics, setMetrics] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      const [metricsRes, weeklyRes] = await Promise.all([
        api.get('/api/reports/metrics'),
        api.get('/api/reports/weekly-summary')
      ]);
      setMetrics(metricsRes.data);
      setWeeklySummary(weeklyRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await api.get(`/api/reports/export?format=${format}`);
      if (format === 'csv') {
        const blob = new Blob([res.data.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = res.data.filename; a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `hr_catalyst_report_${new Date().toISOString().slice(0,10)}.json`; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) { console.error(err); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  const barData = (metrics?.agents || []).map(a => ({
    name: a.name,
    tasks: a.metrics?.tasks_completed || 0,
    messages: a.metrics?.messages_sent || 0,
    hours: a.metrics?.hours_saved || 0,
  }));

  const pieData = (metrics?.agents || []).map(a => ({
    name: a.name,
    value: (a.metrics?.tasks_completed || 0) + (a.metrics?.messages_sent || 0),
  })).filter(d => d.value > 0);

  return (
    <div className="reports-page fade-in">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Performance Reports</h3>
          <p style={{ fontSize: 14, color: '#64748b' }}>Track your AI employees' performance and impact on your HR operations.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => handleExport('csv')} disabled={exporting} data-testid="export-csv">
            <Download size={14} />Export CSV
          </button>
          <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => handleExport('json')} disabled={exporting} data-testid="export-json">
            <Download size={14} />Export JSON
          </button>
        </div>
      </div>

      {/* Weekly Summary */}
      {weeklySummary && weeklySummary.total_tasks > 0 && (
        <div className="chart-card" style={{ borderLeft: '4px solid var(--panzix-green)', marginBottom: 20 }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={18} color="var(--panzix-green)" />Weekly Summary ({weeklySummary.period})</h4>
          <div style={{ display: 'flex', gap: 24, marginTop: 12, flexWrap: 'wrap' }}>
            {weeklySummary.highlights?.map((h, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151' }}>
                <CheckCircle size={14} color="#22c55e" />{h}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>
            <Users size={22} color="#16a34a" />
          </div>
          <div className="stat-value">{metrics?.totals?.active_agents || 0}</div>
          <div className="stat-label">Active Agents</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)' }}>
            <CheckCircle size={22} color="#4f46e5" />
          </div>
          <div className="stat-value">{metrics?.totals?.tasks_completed || 0}</div>
          <div className="stat-label">Total Tasks</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
            <TrendingUp size={22} color="#d97706" />
          </div>
          <div className="stat-value">{metrics?.totals?.messages_sent || 0}</div>
          <div className="stat-label">Messages</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fce7f3, #fbcfe8)' }}>
            <Clock size={22} color="#db2777" />
          </div>
          <div className="stat-value">{metrics?.totals?.hours_saved || 0}</div>
          <div className="stat-label">Hours Saved</div>
        </div>
      </div>

      {/* Charts */}
      {barData.length > 0 ? (
        <>
          <div className="chart-card">
            <h4>Agent Performance Comparison</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" fontSize={13} />
                <YAxis fontSize={13} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="tasks" fill="#2E7D32" radius={[6, 6, 0, 0]} name="Tasks" />
                <Bar dataKey="messages" fill="#7B1FA2" radius={[6, 6, 0, 0]} name="Messages" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {pieData.length > 0 && (
            <div className="chart-card">
              <h4>Activity Distribution</h4>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                    {pieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Activity Timeline */}
          {metrics?.timeline?.length > 0 && (
            <div className="chart-card">
              <h4>Recent Task Activity</h4>
              <table className="audit-log-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Agent</th>
                    <th>Task</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.timeline.map((t, i) => (
                    <tr key={t.date + t.agent + i}>
                      <td>{t.date}</td>
                      <td style={{ fontWeight: 600 }}>{t.agent}</td>
                      <td>{t.task}</td>
                      <td>
                        <span className={`agent-status ${t.status === 'completed' ? 'active' : 'paused'}`} style={{ fontSize: 11 }}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <TrendingUp size={64} />
          <h4>No Data Yet</h4>
          <p>Deploy AI employees and start running tasks to see performance metrics here.</p>
        </div>
      )}
    </div>
  );
}
